import React from 'react'
import { Button } from './ui/button'
import { 
  CheckCircle, 
  Calendar, 
  MapPin, 
  Users, 
  CreditCard, 
  Mail, 
  Phone, 
  Download,
  Share,
  X
} from 'lucide-react'

interface BookingConfirmationProps {
  isOpen: boolean
  onClose: () => void
  bookingData: {
    bookingId: string
    bookingReference?: string
    itemName: string
    itemType: string
    locationName?: string
    checkInDate?: string
    checkOutDate?: string
    guests: {
      adults: number
      children: number
      infants: number
    }
    totalPrice: number
    currency: string
    guestName: string
    guestEmail: string
    guestPhone?: string
    passengerDetails?: {
      primaryGuest: {
        firstName: string
        lastName: string
        email: string
        phone?: string
        dateOfBirth?: string
        nationality?: string
      }
      additionalGuests?: Array<{
        firstName: string
        lastName: string
        dateOfBirth?: string
        nationality?: string
      }>
    }
  }
  paymentData: {
    payment_intent_id: string
    amount: number
    currency: string
    status: string
  }
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  isOpen,
  onClose,
  bookingData,
  paymentData
}) => {
  if (!isOpen) return null

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getItemTypeIcon = () => {
    switch (bookingData.itemType) {
      case 'room':
      case 'accommodation':
        return '🏨'
      case 'activity':
        return '🎯'
      case 'package':
        return '📦'
      default:
        return '✈️'
    }
  }

  const handleDownloadConfirmation = () => {
    // Create a simple text file with booking details
    const confirmationText = `
BOOKING CONFIRMATION
===================

Booking Reference: ${bookingData.bookingReference || bookingData.bookingId}
Item: ${bookingData.itemName}
Type: ${bookingData.itemType}
Location: ${bookingData.locationName || 'N/A'}

GUEST DETAILS
=============
Primary Guest: ${bookingData.passengerDetails?.primaryGuest.firstName} ${bookingData.passengerDetails?.primaryGuest.lastName}
Email: ${bookingData.guestEmail}
Phone: ${bookingData.guestPhone || 'N/A'}

TRAVEL DETAILS
==============
Check-in: ${formatDate(bookingData.checkInDate)}
Check-out: ${formatDate(bookingData.checkOutDate)}
Guests: ${bookingData.guests.adults} adults, ${bookingData.guests.children} children, ${bookingData.guests.infants} infants

PAYMENT DETAILS
===============
Total Amount: ${formatCurrency(bookingData.totalPrice, bookingData.currency)}
Payment ID: ${paymentData.payment_intent_id}
Payment Status: ${paymentData.status}

Thank you for your booking!
    `.trim()

    const blob = new Blob([confirmationText], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `booking-confirmation-${bookingData.bookingReference || bookingData.bookingId}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleShareConfirmation = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Booking Confirmation',
        text: `Booking confirmed! Reference: ${bookingData.bookingReference || bookingData.bookingId} for ${bookingData.itemName}`,
        url: window.location.href
      })
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(
        `Booking confirmed! Reference: ${bookingData.bookingReference || bookingData.bookingId} for ${bookingData.itemName}. Total: ${formatCurrency(bookingData.totalPrice, bookingData.currency)}`
      )
      alert('Booking details copied to clipboard!')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Booking Confirmed!</h2>
              <p className="text-gray-600">Your reservation has been successfully processed</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="w-8 h-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Booking Reference */}
          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="text-lg font-semibold text-green-900 mb-2">Booking Reference</h3>
            <p className="text-2xl font-bold text-green-800 font-mono">{bookingData.bookingReference || bookingData.bookingId}</p>
            <p className="text-sm text-green-700 mt-1">Please save this reference number for your records</p>
          </div>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-2xl">{getItemTypeIcon()}</span>
              Booking Details
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="font-medium text-gray-900 mb-2">{bookingData.itemName}</h5>
                <div className="space-y-2 text-sm text-gray-600">
                  {bookingData.locationName && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{bookingData.locationName}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(bookingData.checkInDate)} - {formatDate(bookingData.checkOutDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{bookingData.guests.adults} adults, {bookingData.guests.children} children, {bookingData.guests.infants} infants</span>
                  </div>
                </div>
              </div>
              
              <div className="text-right md:text-left">
                <h5 className="font-medium text-gray-900 mb-2">Total Amount</h5>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(bookingData.totalPrice, bookingData.currency)}
                </p>
                <p className="text-sm text-gray-600">All taxes included</p>
              </div>
            </div>
          </div>

          {/* Guest Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">Guest Information</h4>
            
            {/* Primary Guest */}
            <div className="mb-4">
              <h5 className="font-medium text-gray-800 mb-2">Primary Guest</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Name:</strong> {bookingData.passengerDetails?.primaryGuest.firstName} {bookingData.passengerDetails?.primaryGuest.lastName}</p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {bookingData.guestEmail}
                  </p>
                  {bookingData.guestPhone && (
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {bookingData.guestPhone}
                    </p>
                  )}
                </div>
                <div>
                  {bookingData.passengerDetails?.primaryGuest.dateOfBirth && (
                    <p><strong>Date of Birth:</strong> {bookingData.passengerDetails.primaryGuest.dateOfBirth}</p>
                  )}
                  {bookingData.passengerDetails?.primaryGuest.nationality && (
                    <p><strong>Nationality:</strong> {bookingData.passengerDetails.primaryGuest.nationality}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Guests */}
            {bookingData.passengerDetails?.additionalGuests && bookingData.passengerDetails.additionalGuests.length > 0 && (
              <div>
                <h5 className="font-medium text-gray-800 mb-2">Additional Guests</h5>
                <div className="space-y-2">
                  {bookingData.passengerDetails.additionalGuests.map((guest, index) => (
                    <div key={index} className="text-sm bg-white p-2 rounded border">
                      <p><strong>Guest {index + 2}:</strong> {guest.firstName} {guest.lastName}</p>
                      {guest.dateOfBirth && <p><strong>Date of Birth:</strong> {guest.dateOfBirth}</p>}
                      {guest.nationality && <p><strong>Nationality:</strong> {guest.nationality}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Information
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Payment Status:</strong> 
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                    {paymentData.status === 'succeeded' ? 'Paid' : paymentData.status}
                  </span>
                </p>
                <p><strong>Amount Paid:</strong> {formatCurrency(paymentData.amount, paymentData.currency)}</p>
              </div>
              <div>
                <p><strong>Transaction ID:</strong></p>
                <p className="font-mono text-xs bg-white p-1 rounded border break-all">
                  {paymentData.payment_intent_id}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={handleDownloadConfirmation}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Confirmation
            </Button>
            <Button 
              onClick={handleShareConfirmation}
              variant="outline"
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Share className="w-4 h-4" />
              Share
            </Button>
            <Button 
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Close
            </Button>
          </div>

          {/* Important Notes */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Important Information</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• A confirmation email has been sent to {bookingData.guestEmail}</li>
              <li>• Please arrive 15 minutes before your scheduled time</li>
              <li>• Bring a valid ID that matches the primary guest name</li>
              <li>• Contact us at least 24 hours in advance for any changes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingConfirmation